import React, { useEffect, useState } from 'react';
import { SettingForm } from '../Forms/SettingForm';
import { useNavigate } from 'react-router-dom';
import { getAllSettingsService } from './services/Settings.service';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import SettingsTable from './components/SettingsTable';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import StressTesting from './components/StressTesting';
import FileMigration from './components/FileMigration';
import PDFMigration from './components/PDFMigration';
import OrderStatusBackfill from './components/OrderStatusBackfill';
import FulfillmentRepair from './components/FulfillmentRepair';
import FulfillmentConnectivity from './components/FulfillmentConnectivity';
import TestData from '../TestData/TestData';
import ProjectStudentMigration from './components/ProjectStudentMigration';
import PrintDispatchSettings from './components/PrintDispatchSettings';
import OrderTrackingSettings from './components/OrderTrackingSettings';
import OrderNotificationsSettings from './components/OrderNotificationsSettings';
import { getPlans } from '../Plans/services/Plans.service';
import { PlanDetailsModel } from '../../../interfaces/plan.interface';

const SETTINGS_TABS = ['General', 'Fulfillment', 'Data & Migrations', 'Testing'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];

export interface SettingModel {
  id: string;
  settingTitle: string;
  settingValue: string | number | boolean;
  settingValueList?: string[] | Array<{ title: string; value: string }>;
}

const Settings = () => {
  const [loader, setLoader] = useState(false);
  const [settings, setAllSettings] = useState<SettingModel[]>([]);
  const [plans, setPlans] = useState<PlanDetailsModel[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [settingDetails, setSettingDetails] = useState<any | null>(null);
  const [tab, setTab] = useState<SettingsTab>('General');
  const navigate = useNavigate();
  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const fetchAllSettings = async () => {
    try {
      setLoader(true);
      const settings = await getAllSettingsService();
      setAllSettings(settings);
    } catch (error) {
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchAllSettings();
    getPlans(setPlans, null);
  }, []);

  const handleEditSetting = (settingKey: string) => {
    const settingInfo = settings.filter((val) => val.id === settingKey);
    setSettingDetails(settingInfo);
    setShowForm(true);
  };

    return (
        <div className="p-0 md:p-4">
            {loader ? (
                <LoadingSpinner />
            ) : showForm ? (
                <SettingForm
                    setShowForm={setShowForm}
                    getAllSettings={fetchAllSettings}
                    settingDetails={settingDetails}
                    plans={plans}
                />
            ) : (
              <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-1 border-b border-gray-200">
                    {SETTINGS_TABS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                          tab === t
                            ? 'border-storyblue text-storyblue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  {tab === 'General' && <SettingsTable settings={settings} onEditSetting={handleEditSetting} />}
                  {tab === 'Fulfillment' && (
                    <div className="flex flex-col gap-4">
                      <PrintDispatchSettings />
                      <OrderTrackingSettings />
                      <OrderNotificationsSettings />
                    </div>
                  )}
                  {tab === 'Data & Migrations' && (
                    <div className="flex flex-col gap-4">
                      <FileMigration />
                      <PDFMigration />
                      <OrderStatusBackfill />
                      <FulfillmentRepair />
                      <ProjectStudentMigration />
                    </div>
                  )}
                  {tab === 'Testing' && (
                    <div className="flex flex-col gap-4">
                      {/* Safe on every environment, production included — read-only probes. */}
                      <FulfillmentConnectivity />
                      {/* TestData self-hides outside dev (server-decided availability). */}
                      <TestData />
                      <StressTesting />
                    </div>
                  )}
              </div>
            )}
        </div>
    );
};
export default Settings;
